import {
  ImageLightbox,
  type MessageResponseProps,
  markdownRehypePlugins,
} from '@crowdex/ui/ai/message';
import { ImageOff } from 'lucide-react';
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { artifactContent } from '@/features/apps/artifacts-client';
import { imageSource, workspaceImageType } from '@/lib/conversations/workspace-image-source';

/**
 * Reads an image of the conversation's workspace and gives a URL the page can show (a blob URL),
 * or rejects when it cannot be read. `root` is the workspace's absolute path when it is known (a
 * reply may cite an absolute path inside it).
 */
export type WorkspaceImages = {
  /** Identifies what `load` reads from (the conversation): a cache key. */
  key: string;
  root: string | null;
  load: (path: string) => Promise<string>;
};

const WorkspaceImagesContext = createContext<WorkspaceImages | null>(null);

export function WorkspaceImagesProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: WorkspaceImages | null;
}) {
  return (
    <WorkspaceImagesContext.Provider value={value}>{children}</WorkspaceImagesContext.Provider>
  );
}

/** A workspace image larger than this is not shown in a reply. */
const MAX_IMAGE_BYTES = 12 * 1024 * 1024;

function decodeBase64(content: string): Uint8Array<ArrayBuffer> {
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/**
 * A conversation's workspace images, read through its file space (`/api/artifacts/content`):
 * signed in, only that conversation's workspace, the server refusing paths that leave it.
 */
export function conversationWorkspaceImages(
  conversation: string,
  root: string | null,
): WorkspaceImages {
  return {
    key: conversation,
    root,
    async load(path) {
      const type = workspaceImageType(path);
      if (type === null) throw new Error('Not an image.');
      const file = await artifactContent({
        space: 'workspace',
        path,
        plugin: 'core',
        conversation,
      });
      // An image read as text (what a sleeping execution last reported) is not its bytes.
      if (file.encoding !== 'base64' && type !== 'image/svg+xml')
        throw new Error('Not readable as an image.');
      const bytes =
        file.encoding === 'base64'
          ? decodeBase64(file.content)
          : new TextEncoder().encode(file.content);
      if (bytes.byteLength > MAX_IMAGE_BYTES) throw new Error('Too large to show.');
      return URL.createObjectURL(new Blob([bytes], { type }));
    },
  };
}

/** Loaded images by source and path, so a re-render (or another message) does not read again. */
const loaded = new Map<string, Promise<string>>();
const MAX_CACHED = 64;

function loadOnce(images: WorkspaceImages, path: string): Promise<string> {
  const key = `${images.key}\0${path}`;
  const known = loaded.get(key);
  if (known !== undefined) return known;
  const pending = images.load(path);
  loaded.set(key, pending);
  pending.catch(() => loaded.delete(key));
  if (loaded.size > MAX_CACHED) {
    const [oldest, url] = loaded.entries().next().value as [string, Promise<string>];
    loaded.delete(oldest);
    void url.then(
      (value) => value.startsWith('blob:') && URL.revokeObjectURL(value),
      () => {},
    );
  }
  return pending;
}

/** Forget the cached images (tests). */
export function resetWorkspaceImages(): void {
  loaded.clear();
}

function Unavailable({ alt }: { alt: string }) {
  return (
    <span
      aria-label={alt ? `Image unavailable: ${alt}` : 'Image unavailable'}
      className="inline-flex items-center gap-1.5 rounded-md border border-border border-dashed px-2 py-1 align-middle text-muted-foreground text-xs"
      role="img"
    >
      <ImageOff aria-hidden className="size-3.5 shrink-0" />
      Image unavailable
    </span>
  );
}

function Shown({ alt, src, onError }: { alt: string; src: string; onError?: () => void }) {
  return (
    <ImageLightbox className="inline-block max-w-full align-top" name={alt || 'Image'} src={src}>
      <img
        alt={alt}
        className="max-h-80 max-w-full rounded-lg border border-border object-contain"
        decoding="async"
        loading="lazy"
        onError={onError}
        src={src}
      />
    </ImageLightbox>
  );
}

/**
 * An image of a reply's markdown: a remote image as the page loads it, a workspace file through
 * the conversation's file space, else a quiet "Image unavailable". Each opens full size.
 */
function MarkdownImage({
  src,
  alt,
  ...props
}: ComponentProps<'img'> & { node?: unknown; 'data-workspace-src'?: string }) {
  const images = useContext(WorkspaceImagesContext);
  const text = typeof alt === 'string' ? alt : '';
  const cited = props['data-workspace-src'] ?? (typeof src === 'string' ? src : undefined);
  const source = imageSource(cited, images?.root ?? null);
  const path = source.kind === 'workspace' ? source.path : null;
  const [state, setState] = useState<{ path: string; url: string | null } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (path === null || images === null) return;
    let live = true;
    loadOnce(images, path).then(
      (url) => live && setState({ path, url }),
      () => live && setState({ path, url: null }),
    );
    return () => {
      live = false;
    };
  }, [images, path]);

  if (source.kind === 'remote')
    return failed ? (
      <Unavailable alt={text} />
    ) : (
      <Shown alt={text} onError={() => setFailed(true)} src={source.url} />
    );
  if (source.kind === 'unavailable' || images === null) return <Unavailable alt={text} />;
  if (state === null || state.path !== path)
    return (
      <span
        aria-label={text ? `Loading ${text}` : 'Loading image'}
        className="inline-block h-24 w-32 animate-pulse rounded-lg bg-muted align-top motion-reduce:animate-none"
        role="img"
      />
    );
  if (state.url === null) return <Unavailable alt={text} />;
  return <Shown alt={text} src={state.url} />;
}

/** What a workspace image's `src` reads until `MarkdownImage` resolves it (harden allows it). */
const WORKSPACE_IMAGE_PLACEHOLDER = 'blob:crowdex-workspace-image';
const WORKSPACE_SRC = 'dataWorkspaceSrc';

type HastNode = {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

/**
 * Keeps the path a reply cites for an image (`avatars/photo.jpg`) past Streamdown's hardening,
 * which only lets remote URLs through: the path moves to `data-workspace-src` and the image reads
 * through the conversation's file space (`MarkdownImage`), which refuses what leaves it.
 */
function rehypeWorkspaceImages() {
  const walk = (node: HastNode) => {
    if (node.type === 'element' && node.tagName === 'img') {
      const properties = node.properties ?? {};
      const src = typeof properties['src'] === 'string' ? properties['src'] : '';
      if (!/^(https?:|data:image\/|blob:)/i.test(src)) {
        node.properties = { ...properties, [WORKSPACE_SRC]: src, src: WORKSPACE_IMAGE_PLACEHOLDER };
      }
    }
    for (const child of node.children ?? []) walk(child);
  };
  return (tree: HastNode) => walk(tree);
}

/** Streamdown's rehype plugins with workspace images kept between sanitizing and hardening. */
const REPLY_REHYPE_PLUGINS = Object.entries(markdownRehypePlugins).flatMap(([name, plugin]) =>
  name === 'harden' ? [rehypeWorkspaceImages, plugin] : [plugin],
);

/** Streamdown options for a conversation's replies: their images resolve as above. */
export const REPLY_MARKDOWN: Pick<MessageResponseProps, 'components' | 'rehypePlugins'> = {
  components: { img: MarkdownImage },
  rehypePlugins: REPLY_REHYPE_PLUGINS,
};
